import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import { type Content, EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { ChevronLeft, Download, Trash2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { UnsavedChangesDialog } from "../canvas-kit/ui/unsaved-changes-dialog.jsx";
import { useExportSheet } from "../export-sheet/index.js";
import { NoteExportDialog } from "./export-dialog.jsx";
import { noteDocToMarkdown } from "./export-markdown.js";
import { noteDocToPdfBytes } from "./export-pdf.js";
import { emptyNoteSnapshot, type NoteSnapshot } from "./snapshot.js";
import { NoteToolbar } from "./toolbar.jsx";
import "./tiptap-styles.css";

/** Normalize a free-form title into a safe download filename stem. */
function sanitizeFilename(input: string): string {
  return input
    .normalize("NFKD")
    .replace(/[^\p{L}\p{N}_ -]+/gu, "")
    .trim()
    .replace(/\s+/g, "-")
    .slice(0, 80);
}

/**
 * Markdown-style note editor.
 *
 * The note feature lives next to whiteboard + tactics but uses a totally
 * different surface: a single max-w content column with a TipTap-powered
 * rich-text editor. Storage format is the editor's native JSON doc (lossless
 * round-trip) — a Markdown export can be layered on later without changing
 * how we persist.
 *
 * Dirty tracking + the shared `UnsavedChangesDialog` mirror the whiteboard /
 * tactics behaviour so the back button can offer the same three exit paths.
 */
export function NoteEditor({
  name,
  snapshot,
  saving,
  onSave,
  onClose,
  onDelete,
}: {
  name: string;
  snapshot: NoteSnapshot;
  saving: boolean;
  onSave: (next: { name: string; snapshot: NoteSnapshot }) => void;
  onClose: () => void;
  /** Optional delete handler — when provided, surfaces a Trash button in the topbar. */
  onDelete?: () => void;
}) {
  const [draftName, setDraftName] = useState(name);
  const [dirty, setDirty] = useState(false);
  const [confirmExitOpen, setConfirmExitOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const { openExportSheet, sheet: exportSheet } = useExportSheet();

  /**
   * TipTap editor instance. `content` is only consumed on first mount;
   * subsequent updates to the `snapshot` prop are handled by the replace
   * effect below (mirrors how the whiteboard editor adopts a fresh snapshot
   * unless local edits are pending).
   */
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Disable items we don't surface in the toolbar to keep keystrokes
        // predictable. Drop these as we add the corresponding buttons.
        heading: { levels: [1, 2, 3] },
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: {
          rel: "noopener noreferrer nofollow",
          target: "_blank",
        },
      }),
      Placeholder.configure({
        placeholder: "Tippe hier los…",
      }),
    ],
    content: (snapshot.doc ?? emptyNoteSnapshot().doc) as Content,
    editorProps: {
      attributes: {
        // ProseMirror surface — picks up `.note-prose` styles.
        class: "note-prose min-h-[60vh] focus:outline-none",
      },
    },
    onUpdate: () => {
      setDirty(true);
    },
  });

  // Replace the in-editor doc if the parent passes a brand-new snapshot
  // (e.g. user switches to another note via the URL) — but never overwrite
  // unsaved local edits. Mirrors the whiteboard editor's `dirtyRef` guard.
  const dirtyRef = useRef(false);
  dirtyRef.current = dirty;
  useEffect(() => {
    if (!editor || dirtyRef.current) return;
    editor.commands.setContent((snapshot.doc ?? emptyNoteSnapshot().doc) as Content, {
      emitUpdate: false,
    });
    setDraftName(name);
    // We deliberately depend on the snapshot reference, not its contents.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshot, editor]);

  /**
   * Build a NoteSnapshot from the current editor state, including a short
   * plain-text preview for the overview card. Called by handleSave and by
   * the "Speichern & Verlassen" branch of the unsaved-changes dialog.
   */
  const buildSnapshot = useCallback((): NoteSnapshot => {
    if (!editor) return snapshot;
    const doc = editor.getJSON();
    const text = editor.getText().replace(/\s+/g, " ").trim();
    return {
      v: 1,
      doc,
      preview: text.length > 0 ? text.slice(0, 280) : null,
    };
  }, [editor, snapshot]);

  const handleSave = useCallback(() => {
    onSave({
      name: draftName.trim() || name,
      snapshot: buildSnapshot(),
    });
    setDirty(false);
  }, [buildSnapshot, draftName, name, onSave]);

  const requestClose = useCallback(() => {
    if (!dirty) {
      onClose();
      return;
    }
    setConfirmExitOpen(true);
  }, [dirty, onClose]);

  // Cmd/Ctrl+S to save — matches the muscle memory of the other editors.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const meta = e.metaKey || e.ctrlKey;
      if (meta && e.key.toLowerCase() === "s") {
        e.preventDefault();
        if (dirty) handleSave();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [dirty, handleSave]);

  return (
    <div className="flex h-full flex-col gap-3 px-3 pb-3 pt-3 sm:px-4">
      {/* Topbar */}
      <div className="glass-floating flex flex-wrap items-center gap-3 rounded-3xl px-4 py-3">
        <button
          type="button"
          onClick={requestClose}
          className="inline-flex h-9 items-center gap-1 rounded-full bg-white/6 px-3 text-body-sm hover:bg-white/10"
          aria-label="Zurück zur Übersicht"
        >
          <ChevronLeft className="size-4" />
          Übersicht
        </button>
        <input
          value={draftName}
          onChange={(e) => {
            setDraftName(e.target.value);
            setDirty(true);
          }}
          className="h-9 min-w-45 flex-1 rounded-full border border-white/15 bg-white/4 px-3 text-body-sm outline-none focus:border-pitch-lime/60 focus:shadow-glow"
          placeholder="Notiz-Titel…"
        />
        {dirty && (
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-400/10 px-2.5 py-1 text-[11px] uppercase tracking-widest text-amber-300">
            Ungespeichert
          </span>
        )}
        <button
          type="button"
          onClick={handleSave}
          disabled={!dirty || saving}
          className="inline-flex h-9 items-center gap-1 rounded-full bg-pitch-lime px-4 text-body-sm font-semibold text-[#1d3700] hover:shadow-glow disabled:opacity-50"
        >
          {saving ? "Speichere…" : "Speichern"}
        </button>
        <button
          type="button"
          onClick={() => setExportOpen(true)}
          className="inline-flex h-9 items-center gap-1 rounded-full bg-white/6 px-3 text-body-sm hover:bg-white/10"
          aria-label="Notiz exportieren"
          title="Exportieren (Markdown / PDF)"
        >
          <Download className="size-4" />
          Export
        </button>
        {onDelete && (
          <button
            type="button"
            onClick={onDelete}
            className="inline-flex h-9 items-center gap-1 rounded-full bg-rose-500/15 px-3 text-body-sm font-medium text-rose-300 hover:bg-rose-500/25"
            aria-label="Notiz löschen"
          >
            <Trash2 className="size-4" />
            Löschen
          </button>
        )}
      </div>

      {/* Toolbar */}
      <div className="flex justify-center">
        <NoteToolbar editor={editor} />
      </div>

      {/* Editor surface — single comfortable column */}
      <div className="flex flex-1 justify-center overflow-y-auto pb-12">
        <div className="glass-floating w-full max-w-3xl rounded-3xl px-8 py-10 sm:px-12 sm:py-12">
          <EditorContent editor={editor} />
        </div>
      </div>

      {exportOpen && editor && (
        <NoteExportDialog
          defaultFilename={sanitizeFilename(draftName.trim() || name || "notiz")}
          onClose={() => setExportOpen(false)}
          onConfirm={async ({ format, filename }) => {
            const doc = editor.getJSON();
            const safe = sanitizeFilename(filename) || "notiz";
            const title = draftName.trim() || name || "Notiz";
            if (format === "md") {
              const md = noteDocToMarkdown(doc);
              const body = `# ${title}\n\n${md}`;
              const bytes = new TextEncoder().encode(body);
              setExportOpen(false);
              await openExportSheet({
                bytes,
                filename: `${safe}.md`,
                mime: "text/markdown",
                title: "Notiz exportieren",
              });
            } else {
              const bytes = await noteDocToPdfBytes(doc, title);
              setExportOpen(false);
              await openExportSheet({
                bytes,
                filename: `${safe}.pdf`,
                mime: "application/pdf",
                title: "Notiz exportieren",
              });
            }
          }}
        />
      )}

      <UnsavedChangesDialog
        open={confirmExitOpen}
        saving={saving}
        onSaveAndClose={() => {
          handleSave();
          setConfirmExitOpen(false);
          onClose();
        }}
        onDiscard={() => {
          setConfirmExitOpen(false);
          onClose();
        }}
        onCancel={() => setConfirmExitOpen(false)}
      />

      {exportSheet}
    </div>
  );
}
